import type { QueryServiceSuspendedBy } from '../utils/suspendedBy';

/**
 * QueryService types and constants
 */

export type QueryServiceStatus =
  | 'ready'
  | 'running'
  | 'suspended'
  | 'deploying'
  | 'error'
  | 'pending'
  | 'creating'
  | 'updating'
  | 'suspending'
  | 'resuming'
  | 'deleting';

export interface ConnectivityEndpoint {
  domain: string;
  port: number;
}

export interface ConnectivityDetails {
  grpcQuery?: ConnectivityEndpoint;
  httpQuery?: ConnectivityEndpoint;
  gatewayQuery?: ConnectivityEndpoint;
}

export interface QueryService {
  // Index signature for RowData compatibility
  [key: string]: unknown;

  name: string;

  // Status and metadata
  status: QueryServiceStatus | string;
  message?: string;
  /** Who suspended a suspended cluster: "user", "auto" or "workspace" (CO-127). */
  suspendedBy?: QueryServiceSuspendedBy | string;
  ready?: boolean;
  createdAt?: string;
  updatedAt?: string;

  // Audit attribution (populated by the API when annotations are present
  // on the underlying CRD; may be absent on resources that pre-date the
  // audit-stamping feature or were created by a system/m2m flow).
  createdByEmail?: string;
  updatedByEmail?: string;

  // Structured creator identity. A machine (OAuth2 client-credentials) or
  // e6_svc_ service-token creator carries NO email — the API instead sends
  // createdByType="service_account" plus the opaque client/token id in
  // createdById, so the "Created by" column can attribute it instead of
  // rendering a blank placeholder. Humans keep createdByEmail; createdByType
  // is absent (or "user") for them.
  createdById?: string;
  createdByType?: string;

  // Release information
  releaseRef?: string;
  releaseVersion?: string; // Display version for the UI

  // A fingerprint of the settings a user can change (detail response only).
  // An edit sends it back so the API refuses the update if the cluster's
  // settings changed since the form was loaded.
  configVersion?: string;

  // Query timeout
  queryTimeoutSeconds?: number;

  // Autoscaling configuration
  autoscalingEnabled?: boolean;
  minExecutors?: number;
  maxExecutors?: number;
  windowBasedEnabled?: boolean;
  slidingWindowDuration?: number;

  // Auto-suspension configuration
  autoSuspensionEnabled?: boolean;
  maxIdleDurationMinutes?: number;

  // Query history
  queryHistoryEnabled?: boolean;

  // Auto resume
  autoResumeEnabled?: boolean;

  // Result set cache
  queryResultCacheEnabled?: boolean;
  queryResultCacheTTLSeconds?: number;

  // Transpiler
  transpilerEnabled?: boolean;
  transpilerDialect?: string;

  // Runtime status
  currentExecutors?: number;
  executorReplicas?: number;
  // Executors actually Ready (running + passing readiness probes), from CR
  // status via compute-api (PLT-9852). Unlike executorReplicas (the desired
  // spec target), this lags during ramps. Undefined when the backend predates
  // the field — consumers must fall back to executorReplicas.
  readyExecutors?: number;

  // Executor count of the size the user chose at creation (XS=1, M=4, custom=N),
  // persisted independently of autoscaling bounds. The UI maps it to a label
  // (preset → XS/S/M/…, otherwise → "Custom (N)"). Undefined for legacy clusters.
  baseSizeExecutors?: number;
  // UI-only display label derived from baseSizeExecutors by useQueryServices
  // (e.g. "XS", "Custom (3)"). Not persisted; powers the Size column, search,
  // and the size filter so they share one value.
  sizeLabel?: string;

  // QPS (Queries Per Second) configuration
  // Flat fields (normalised by the API layer from the nested `qps` object)
  qpsEnabled?: boolean;
  qpsCount?: number;
  plannerCores?: number;
  calculatedPlannerCount?: number;
  // Nested shape returned by the backend API
  qps?: { enabled?: boolean; count?: number };

  // Connectivity details
  connectivityDetails?: ConnectivityDetails;

  // Cluster error classification. Detail responses carry both fields; list
  // rows carry only `error` (PLT-10701). `error` is a convenience pointer to
  // `errors[0]` when one exists.
  error?: ClusterError;
  errors?: ClusterError[];

  // Internal diagnostics — populated only for e6 internal staff. Customer
  // and service-account responses never include this field.
  internalDiagnostics?: InternalClusterDiagnostics;
}

export type ClusterErrorSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';

/**
 * Customer-facing cluster error. Every field is safe to render directly
 * to end users — the API guarantees no internal vocabulary leaks.
 */
export interface ClusterError {
  code: string;
  title: string;
  message: string;
  customerAction: string;
  severity: ClusterErrorSeverity;
  retryable: boolean;
  terminal: boolean;
  supportReference: string;
  // Id of the cluster action that failed, which support can search in logs
  // and traces. Absent when the API can no longer tie the error to one
  // action, e.g. when a later action already ran.
  operationId?: string;
  firstSeenAt?: string;
  lastSeenAt?: string;
}

/**
 * Internal e6-staff view of a cluster error. Extends ClusterError with
 * support-facing context, raw Kubernetes signals, and engineering
 * diagnostics. Shown only behind the internal-user gate.
 */
export interface ClusterErrorInternal extends ClusterError {
  category: string;
  componentLogical?: string;
  owner: 'e6' | 'customer' | 'infra' | 'cloudProvider';
  supportMessage: string;
  rawReason?: string;
  rawMessage?: string;
  affectedResources?: ResourceRef[];
  extra?: Record<string, string>;
  eventReferences?: EventReference[];
  occurrences: number;
  childCode?: string;
}

export interface ResourceRef {
  kind: string;
  name: string;
  namespace: string;
  uid: string;
}

export interface EventReference {
  reason: string;
  time: string;
  message?: string;
}

export interface InternalClusterDiagnostics {
  error?: ClusterErrorInternal;
  errors?: ClusterErrorInternal[];
}

export interface QueryServiceFormData {
  name: string;
  namespace: string;
  workspace: string;
  alias?: string;

  // Autoscaling configuration
  autoscalingEnabled?: boolean;
  minExecutors?: number;
  maxExecutors?: number;
  windowBasedEnabled?: boolean;
  slidingWindowDuration?: number;

  // Auto-suspension configuration
  autoSuspensionEnabled?: boolean;
  maxIdleDurationMinutes?: number;

  // Query history
  queryHistoryEnabled?: boolean;

  // Auto resume
  autoResumeEnabled?: boolean;

  // Result set cache
  queryResultCacheEnabled?: boolean;
  queryResultCacheTTLSeconds?: number;

  // Transpiler
  transpilerEnabled?: boolean;
  transpilerDialect?: string;

  // QPS (Queries Per Second)
  qpsEnabled?: boolean;
  qpsCount?: number;

  // Version
  version?: string;
}

export interface QueryServiceListParams {
  page?: number;
  pageSize?: number;
  namespace?: string;
  status?: QueryServiceStatus;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface QueryServiceListResponse {
  items: QueryService[];
  total: number;
}

export interface QueryServiceActionResponse {
  success: boolean;
  message?: string;
  blueGreenTriggered?: boolean;
  warnings?: string[];
}

export interface QpsWarningResponse {
  warning: string;
  code: 'QPS_INSUFFICIENT_CAPACITY';
  details: {
    requestedQPS: number;
    requiredExecutors: number;
    availableExecutors: number;
    hint: string;
  };
}

/**
 * Type guard to check if an API response is a QPS capacity warning
 */
export function isQpsWarningResponse(response: unknown): response is QpsWarningResponse {
  if (!response || typeof response !== 'object') return false;
  const r = response as Record<string, unknown>;
  return (
    r.code === 'QPS_INSUFFICIENT_CAPACITY' &&
    typeof r.warning === 'string' &&
    typeof r.details === 'object' &&
    r.details !== null
  );
}

// Status options for filter dropdowns
export const QUERY_SERVICE_STATUS_OPTIONS = [
  { label: 'Ready', value: 'Ready' },
  { label: 'Running', value: 'Running' },
  { label: 'Suspended', value: 'Suspended' },
  { label: 'Deploying', value: 'Deploying' },
  { label: 'Creating', value: 'Creating' },
  { label: 'Updating', value: 'Updating' },
  { label: 'Suspending', value: 'Suspending' },
  { label: 'Resuming', value: 'Resuming' },
  { label: 'Deleting', value: 'Deleting' },
  { label: 'Error', value: 'Error' },
  { label: 'Pending', value: 'Pending' },
] as const;

// Status badge colors - deprecated, use StatusBadge component instead
// @deprecated Use StatusBadge from '@/components/ui/status-badge' for consistent styling
export const QUERY_SERVICE_STATUS_COLORS: Record<QueryServiceStatus, string> = {
  ready: 'default',
  running: 'default',
  suspended: 'secondary',
  deploying: 'outline',
  creating: 'outline',
  updating: 'outline',
  suspending: 'outline',
  resuming: 'outline',
  deleting: 'outline',
  error: 'destructive',
  pending: 'outline',
};

// Default values for form
export const DEFAULT_QUERY_SERVICE_VALUES: Partial<QueryServiceFormData> = {
  autoscalingEnabled: true,
  minExecutors: 1,
  maxExecutors: 10,
  autoSuspensionEnabled: true,
  maxIdleDurationMinutes: 30,
  queryHistoryEnabled: true,
  version: 'v2.5.0',
};

// Version options for dropdown
export const QUERY_SERVICE_VERSION_OPTIONS = [
  { label: 'v2.6.0 (Latest)', value: 'v2.6.0' },
  { label: 'v2.5.0 (Stable)', value: 'v2.5.0' },
  { label: 'v2.4.0', value: 'v2.4.0' },
  { label: 'v2.3.0', value: 'v2.3.0' },
] as const;
